import {Observable, of} from "rxjs";
import {UserProfileService} from "@shared/services/api/user-profile.service";
import {AceLogger} from "@logging/ace-logger";
import {map, shareReplay, switchMap} from "rxjs/operators";
import {CollectionUtils} from "@shared/util/collections/collection-utils";
import {WebBrowserCommunicationService} from "@shared/services/web-browser/web-browser-communication.service";
import {AngularPageVisibilityService} from "../services/web-browser/page-visibility.service";


interface IntervalRefreshedCache {
  refreshCacheIfNeededAndScheduleRefreshJob: () => void;
}


export abstract class BaseIntervalRefreshedCache<ItemType, IdField extends keyof ItemType> implements IntervalRefreshedCache {

  private readonly REFRESH_INTERVAL = 900000; // 15 minutes
  private readonly REFRESH_INTERVAL_LEEWAY = 5000; // 5 seconds

  private cache: Map<ItemType[IdField], ItemType>;
  private lastRefreshEpoch = 0;
  private fetchFn: () => Observable<ItemType[]>;
  private idField: IdField;
  private refreshJobScheduled = false;
  private activeRefreshRequest: Observable<void> = null;

  private canUseParent = true;

  protected constructor(private pageVisibilityService: AngularPageVisibilityService,
                        private browserCommsService: WebBrowserCommunicationService,
                        private userProfileService: UserProfileService,
                        private logger: AceLogger,
                        private parentBrowserCacheKey: string) {
    this.pageVisibilityService.$onPageVisible.subscribe(() => this.whenPageVisible());
  }

  protected static onPageVisible(cacheReferences: IntervalRefreshedCache[]): void {
    if (cacheReferences?.length) {
      cacheReferences.forEach(cache => {
        if (cache) {
          cache.refreshCacheIfNeededAndScheduleRefreshJob();
        } else {
          console.error('cache object in cache array is not set');
        }
      });
    } else {
      console.error('cache array is empty. cannot schedule cache refresh job.');
    }
  }

  init(fetchFn: () => Observable<ItemType[]>, idField: IdField): void {
    this.fetchFn = fetchFn;
    this.idField = idField;
    this.populateStaticCacheArrayHack();
    this.refreshCacheIfNeededAndScheduleRefreshJob();
  }

  abstract whenPageVisible(): void;

  // Need to keep a static array of concrete class of IntervalRefreshedCache(s) as the Page Visibility events are not fired with reference to IntervalRefreshedCache
  // instance (i.e. 'this' is undefined inside the Page Visibility event listeners). Therefore, need a way to get to the
  // IntervalRefreshedCache instance
  protected abstract populateStaticCacheArrayHack(): void;

  private getCachedValuesById(ids?: ItemType[IdField][]): Map<ItemType[IdField], ItemType> {
    if (ids) {
      const itemsById = new Map<ItemType[IdField], ItemType>();
      for (const id of ids) {
        const item: ItemType = this.cache.get(id);
        if (item) {
          itemsById.set(item[this.idField], item);
        }
      }
      return itemsById;
    } else {
      return new Map(this.cache);
    }
  }

  getItemsById(ids?: ItemType[IdField][], ignoreCache = false): Observable<Map<ItemType[IdField], ItemType>> {
    if (ignoreCache || !this.cache) {
      return this.refreshCache().pipe(map(() => this.getCachedValuesById(ids)));
    } else {
      this.logger.debug('Return items from cache');
      return of(this.getCachedValuesById(ids));
    }
  }

  refreshCacheIfNeededAndScheduleRefreshJob() {
    if (this.pageVisibilityService.isPageVisible()) {
      this.refreshCacheIfNeeded();
      if (!this.refreshJobScheduled) {
        this.refreshJobScheduled = true;
        this.logger.debug('Scheduling cache refresh background job');
        setTimeout(() => {
          this.refreshJobScheduled = false;
          this.refreshCacheIfNeededAndScheduleRefreshJob();
        }, this.REFRESH_INTERVAL);
      } else {
        this.logger.debug('Not scheduling refresh background job as one is already scheduled');
      }
    } else {
      this.refreshJobScheduled = false;
      this.logger.debug('Not scheduling refresh background job as the browser is hidden (likely in background)');
    }
  }

  clearCache(): void {
    this.logger.debug('Clearing cache');
    this.cache = null;
    this.canUseParent = false;
  }

  updateItemIfExists(id: ItemType[IdField], updateFn: (value: ItemType) => ItemType): void {
    const cachedItem = this.cache?.get(id);
    if (cachedItem != null) {
      this.cache.set(id, updateFn(cachedItem));
    }
  }

  private refreshCache(): Observable<void> {
    if (this.fetchFn) {
      this.logger.debug('Attempt to refresh cache');
      if (!this.activeRefreshRequest) {
        const dataFromParent = this.canUseParent && this.parentBrowserCacheKey ? this.browserCommsService.getFromParent(this.parentBrowserCacheKey) : null;
        if (dataFromParent == null) {
          this.logger.debug('Making refresh request');
          this.canUseParent = false;
          this.activeRefreshRequest = this.fetchFn().pipe(
            switchMap(items => {
              const cacheValues = CollectionUtils.indexBy(items, this.idField);
              this.cache = cacheValues;
              this.browserCommsService.update(this.parentBrowserCacheKey, cacheValues);
              this.lastRefreshEpoch = new Date().getTime();
              this.logger.debug('Cache refreshed');
              this.activeRefreshRequest = null;
              return of(null);
            }),
            shareReplay(1)
          );
        } else {
          this.logger.debug('Using cached data from parent browser window for', this.parentBrowserCacheKey);
          this.cache = new Map(dataFromParent);
          return of(null);
        }
      } else {
        this.logger.debug('Returning existing request observable as a refresh request is currently in flight');
      }
      return this.activeRefreshRequest;
    } else {
      throw new Error('Cannot refresh cache as refresh function is not set');
    }
  }

  private refreshCacheIfNeeded() {
    const elapsedTime = new Date().getTime() - this.lastRefreshEpoch;
    const neededElapsed = this.REFRESH_INTERVAL - this.REFRESH_INTERVAL_LEEWAY;

    if (this.userProfileService.getLoggedInUser()) {
      if (elapsedTime >= neededElapsed) {
        if (this.pageVisibilityService.isPageVisible()) {
          this.refreshCache().subscribe(() => {
          });
        } else {
          this.logger.debug('Not refreshing cache as the browser is not visible (likely in the background)');
        }
      } else {
        this.logger.debug('Not refreshing cache as only ' + elapsedTime + ' has passed. Need ' + neededElapsed);
      }
    } else {
      this.logger.debug('Not refreshing cache as the user is not logged in');
    }
  }

}
